import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import {
  clearImportResults,
  CustomerparseCsv,
  importCustomerCsvData,
} from "../../../redux/customer/customerSlice";
import CsvMapping from "./Roles/DIalogBoxes/CsvMapping";
import ImportUI from "./Roles/DIalogBoxes/ImportUI";

const ImportUserPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const fileInputRef = useRef(null);

  const {
    parseCustomerResult,
    importCustomerResult,
    loadingParams,
  } = useSelector((state) => state.customer);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [step, setStep] = useState(1);
  const [mappingSelections, setMappingSelections] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleFileChange = (file) => {
    if (
      file &&
      (file.type === "text/csv" ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".xlsx"))
    ) {
      dispatch(clearImportResults());
      setSelectedFile(file);
      setMappingSelections({});
      return;
    }

    toast.error("Please upload a valid CSV or Excel file");
  };

  const handleNext = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      const result = await dispatch(
        CustomerparseCsv({ userId, file: selectedFile }),
      ).unwrap();

      if (result.success) {
        setMappingSelections(result.data?.suggestedMappings || {});
        setStep(2);
        toast.success("File parsed successfully");
      }
    } catch (error) {
      toast.error(error || "Failed to parse file");
    }
  };

  const handleSelectionChange = (header, value) => {
    setMappingSelections((prev) => ({
      ...prev,
      [header]: value,
    }));
  };

  const handleImport = async () => {
    try {
      const response = await dispatch(
        importCustomerCsvData({
          userId,
          mapping: mappingSelections,
          file: selectedFile,
        }),
      ).unwrap();

      const failedCount = response?.data?.failedCount || 0;

      if (failedCount > 0) {
        toast.success("Imported with some errors. Please check the summary.");
      } else {
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      toast.error(error || "Import failed");
    }
  };

  const csvHeaders = parseCustomerResult?.data?.csvHeaders || [];
  const dbFields = parseCustomerResult?.data?.dbFields || [];
  const sampleRows = parseCustomerResult?.data?.sampleData || [];

  const mappingRows = csvHeaders.map((header) => ({
    header,
    samples: sampleRows
      .map((row) => row[header])
      .filter((value) => value !== undefined),
  }));

  return (
    <div style={{ paddingLeft: "40px", paddingRight: "40px", paddingTop: "20px" }}>
      {step === 1 ? (
        <ImportUI
          heading="Import Users"
          downloadHref={"/samples/User-DOC.csv"}
          downloadName="User-Doc.csv"
          description="Upload a CSV file to import users."
            downloadLabel = "Download Sample"
          onBack={() => navigate(-1)}
          onCancel={() => navigate(-1)}
          supportedTypes={[".csv"]}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          inputRef={fileInputRef}
          onInputChange={(event) => handleFileChange(event.target.files[0])}
          openFileDialog={() => fileInputRef.current?.click()}
          selectedFile={selectedFile}
          onNext={handleNext}
          nextDisabled={!selectedFile || loadingParams.isCustomerParsing}
          nextLabel={loadingParams.isCustomerParsing ? "Parsing..." : "Next"}
          isDragging={isDragging}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            handleFileChange(event.dataTransfer.files[0]);
          }}
        />
      ) : (
        <CsvMapping
          mappingRows={mappingRows}
          mappingSelections={mappingSelections}
          attributeOptions={dbFields}
          onSelectionChange={handleSelectionChange}
          onCancel={() => setStep(1)}
          onImport={handleImport}
          importResult={importCustomerResult}
          isImporting={loadingParams.isCustomerImporting}
          showValidate={false}
        />
      )}

      {showSuccessPopup &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center backdrop-blur-sm">
            <PopupCard
              type="success"
              title="Import Successful"
              message="Users have been imported successfully."
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ImportUserPage;
